import { useState } from "react";
import { CaretDown, CaretRight, FunnelSimple, X } from "@phosphor-icons/react";
import { PERIOD_PRESETS } from "./pcShared";

export function MultiGroup({ label, options, selected, onChange, render, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const vals = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const filtered = search ? vals.filter((v) => v.label.toLowerCase().includes(search.toLowerCase())) : vals;
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="border-b border-zinc-200 dark:border-zinc-700" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-2 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
        <span className="flex items-center gap-1">
          {selected.length > 0 && (
            <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded">{selected.length}</span>
          )}
          {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </span>
      </button>
      {open && (
        <div className="pb-2">
          {vals.length > 8 && (
            <input
              className="input w-full mb-1 !py-1 text-xs"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {filtered.slice(0, 200).map((v) => (
              <label key={v.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 px-1 py-0.5 rounded">
                <input
                  type="checkbox"
                  checked={selected.includes(v.value)}
                  onChange={() => toggle(v.value)}
                />
                <span className="truncate">{render ? render(v) : v.label}</span>
              </label>
            ))}
            {filtered.length === 0 && <div className="text-[10px] text-zinc-400 px-1">Nenhuma opção</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function RangeInputs({ label, minKey, maxKey, draft, set }) {
  return (
    <div className="py-2 border-b border-zinc-200 dark:border-zinc-700">
      <div className="text-xs font-bold uppercase tracking-wide mb-1">{label}</div>
      <div className="flex gap-1">
        <input type="number" className="input w-1/2 !py-1 text-xs" placeholder="Mín"
          value={draft[minKey]} onChange={(e) => set(minKey, e.target.value)} data-testid={`pc-filter-${minKey}`} />
        <input type="number" className="input w-1/2 !py-1 text-xs" placeholder="Máx"
          value={draft[maxKey]} onChange={(e) => set(maxKey, e.target.value)} data-testid={`pc-filter-${maxKey}`} />
      </div>
    </div>
  );
}

export default function FiltersSidebar({ options, draft, setDraft, preset, setPreset, onApply, onClear, onClose }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.range) {
      const [from, to] = p.range();
      setDraft((d) => ({ ...d, date_from: from, date_to: to }));
    }
  };
  return (
    <div className="tile p-3 w-full lg:w-72 shrink-0 lg:sticky lg:top-2 max-h-[calc(100vh-16px)] overflow-y-auto" data-testid="pc-filters-sidebar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-bold text-sm">
          <FunnelSimple size={16} weight="bold" /> Filtros
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-black" data-testid="pc-filters-close">
          <X size={16} />
        </button>
      </div>

      <div className="mb-2">
        <div className="text-xs font-bold uppercase tracking-wide mb-1">Período</div>
        <div className="flex flex-wrap gap-1">
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              className={`text-[10px] px-2 py-1 border rounded ${preset === p.key ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
              data-testid={`pc-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex gap-1 mt-1">
            <input type="date" className="input w-1/2 !py-1 text-xs" value={draft.date_from}
              onChange={(e) => set("date_from", e.target.value)} data-testid="pc-date-from" />
            <input type="date" className="input w-1/2 !py-1 text-xs" value={draft.date_to}
              onChange={(e) => set("date_to", e.target.value)} data-testid="pc-date-to" />
          </div>
        )}
        <div className="text-[10px] text-zinc-500 mt-1 font-mono">{draft.date_from} → {draft.date_to}</div>
      </div>

      <MultiGroup label="Colaborador" testid="pc-filter-collaborators"
        options={(options?.collaborators || []).map((c) => ({ value: c.code || c.name, label: `${c.name}${c.sector ? ` · ${c.sector}` : ""}` }))}
        selected={draft.collaborators} onChange={(v) => set("collaborators", v)} />
      <MultiGroup label="Setor / Equipe" testid="pc-filter-sectors"
        options={options?.sectors || []} selected={draft.sectors} onChange={(v) => set("sectors", v)} />
      <MultiGroup label="Posto de Produção" testid="pc-filter-postos"
        options={(options?.postos || []).map((p) => ({ value: p.status, label: p.label }))}
        selected={draft.postos} onChange={(v) => set("postos", v)} />
      <MultiGroup label="Avançou do Posto" testid="pc-filter-statuses"
        options={(options?.statuses || []).map((p) => ({ value: p.status, label: p.label }))}
        selected={draft.statuses} onChange={(v) => set("statuses", v)} />
      <MultiGroup label="Tipo de Copo" testid="pc-filter-types"
        options={options?.types || []} selected={draft.types} onChange={(v) => set("types", v)} />
      <MultiGroup label="Marketplace" testid="pc-filter-marketplaces"
        options={options?.marketplaces || []} selected={draft.marketplaces} onChange={(v) => set("marketplaces", v)} />
      <MultiGroup label="Loja" testid="pc-filter-stores"
        options={options?.stores || []} selected={draft.stores} onChange={(v) => set("stores", v)} />
      <MultiGroup label="Estado (UF)" testid="pc-filter-states"
        options={options?.states || []} selected={draft.states} onChange={(v) => set("states", v)} />
      <MultiGroup label="Cor do Copo" testid="pc-filter-colors"
        options={options?.colors || []} selected={draft.colors} onChange={(v) => set("colors", v)} />

      <div className="py-2 border-b border-zinc-200 dark:border-zinc-700">
        <div className="text-xs font-bold uppercase tracking-wide mb-1">SKU</div>
        <input className="input w-full !py-1 text-xs" placeholder="POP200, TWS100..." value={draft.skus}
          onChange={(e) => set("skus", e.target.value)} data-testid="pc-filter-skus" />
      </div>
      <div className="py-2 border-b border-zinc-200 dark:border-zinc-700">
        <div className="text-xs font-bold uppercase tracking-wide mb-1">Cidade</div>
        <input className="input w-full !py-1 text-xs" placeholder="São Paulo, Curitiba..." value={draft.cities}
          onChange={(e) => set("cities", e.target.value)} data-testid="pc-filter-cities" />
      </div>
      <div className="py-2 border-b border-zinc-200 dark:border-zinc-700">
        <div className="text-xs font-bold uppercase tracking-wide mb-1">Nº do Pedido</div>
        <input className="input w-full !py-1 text-xs" placeholder="51294" value={draft.order_number}
          onChange={(e) => set("order_number", e.target.value)} data-testid="pc-filter-order-number" />
      </div>
      <RangeInputs label="Quantidade (peças)" minKey="qty_min" maxKey="qty_max" draft={draft} set={set} />
      <RangeInputs label="Valor (R$)" minKey="value_min" maxKey="value_max" draft={draft} set={set} />

      <div className="flex gap-2 mt-3">
        <button className="btn flex-1 !bg-black !text-white" onClick={onApply} data-testid="pc-apply-filters">
          Aplicar
        </button>
        <button className="btn" onClick={onClear} data-testid="pc-clear-filters">
          Limpar
        </button>
      </div>
    </div>
  );
}
